import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowClockwise, EnvelopeSimple, WarningCircle, CheckCircle, ClockClockwise, EyeSlash, Prohibit } from "@phosphor-icons/react";

/**
 * Admin — Email delivery log.
 * 30-day rolling window (TTL enforced server-side on `email_logs.created_at`).
 * Displays every outbound send attempt with status pills. Ready to be enriched
 * later with ZeptoMail /messagestats callbacks (delivered / bounced / opened).
 */
const STATUS_META = {
  sent:    { color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300", icon: CheckCircle },
  failed:  { color: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300", icon: WarningCircle },
  mocked:  { color: "bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300", icon: EyeSlash },
  queued:  { color: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300", icon: ClockClockwise },
  skipped: { color: "bg-slate-100 text-slate-500 dark:bg-slate-800/50 dark:text-slate-400", icon: Prohibit },
};

export default function AdminEmailLog() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (category !== "all") params.set("category", category);
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/email-logs?${params.toString()}`);
      setRows(r.data.items || []);
      setTotals(r.data.totals || {});
    } finally {
      setBusy(false);
    }
  }, [status, category, q]);

  useEffect(() => { load(); }, [load]);

  const categories = Array.from(new Set(rows.map((r) => r.category))).filter(Boolean);

  return (
    <div className="space-y-6" data-testid="admin-email-log">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-1">Deliverability</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Email delivery log</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every outbound send from the last 30 days. Powered by <code className="text-xs px-1 py-0.5 rounded bg-secondary">email_logs</code> (TTL, self-expiring). Wire up ZeptoMail delivery callbacks later to enrich with <b>delivered</b>/<b>bounced</b>/<b>opened</b> events.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="email-log-refresh">
          <ArrowClockwise size={14} className={busy ? "mr-2 animate-spin" : "mr-2"} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(STATUS_META).map(([k, meta]) => {
          const Icon = meta.icon;
          return (
            <div key={k} className={`rounded-xl border border-border p-4 ${meta.color.split(" ").filter(c => c.startsWith("bg-")).join(" ")}`}>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold opacity-80">
                <Icon size={14} weight="fill" /> {k}
              </div>
              <div className="text-2xl font-display font-bold mt-1">{totals[k] ?? 0}</div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Search recipient or subject…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          className="max-w-sm h-9"
          data-testid="email-log-search"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 h-9" data-testid="email-log-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-56 h-9" data-testid="email-log-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border border-border overflow-hidden bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">When</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">To</th>
              <th className="text-left px-4 py-3 font-semibold">Subject</th>
              <th className="text-left px-4 py-3 font-semibold">Category</th>
              <th className="text-left px-4 py-3 font-semibold">Provider</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !busy && (
              <tr><td colSpan={6} className="text-center py-12 text-muted-foreground text-sm">
                <EnvelopeSimple size={24} className="mx-auto mb-2 opacity-40" />
                No email sends match these filters yet.
              </td></tr>
            )}
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.mocked;
              const Icon = meta.icon;
              const when = new Date(r.created_at);
              return (
                <tr key={r.id} className="border-t border-border hover:bg-secondary/40">
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                    {when.toLocaleDateString()} · {when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`${meta.color} border-0`}>
                      <Icon size={12} weight="fill" className="mr-1" /> {r.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{r.to}</td>
                  <td className="px-4 py-3 max-w-md truncate" title={r.subject}>{r.subject}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-secondary">{r.category}</span></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{r.provider || "—"}{r.error ? ` · ${r.error}` : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
